/**
 * Ops route tree, composable under any parent path.
 *
 * Shell mounts it at `/ops/*`; standalone mounts at the repo root. The
 * `opsRoutes(basePath)` factory prefixes sidebar `<Link>`s with the mount
 * path. Route-object paths stay relative so React Router resolves them
 * against the mount point in either mode.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Outlet, useLocation, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { AppShell, type NavItem } from '@widgets/app-shell';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import { QuickNoteFab } from '@widgets/notes';
import { useOpsGroup } from '@entities/ops-group';
import { HeaderGroupSearch } from '../HeaderGroupSearch';
import {
  BoardRegular,
  ClipboardTaskListLtrRegular,
  PeopleRegular,
  ArrowSyncRegular,
  WrenchScrewdriverRegular,
  StarRegular,
} from '@fluentui/react-icons';

const DashboardPage = lazy(() => import('@ops/pages/dashboard').then(m => ({ default: m.DashboardPage })));
const IntakePage = lazy(() => import('@ops/pages/intake').then(m => ({ default: m.IntakePage })));
const GroupsPage = lazy(() => import('@ops/pages/groups').then(m => ({ default: m.GroupsPage })));
const GroupDetailPage = lazy(() => import('@ops/pages/group-detail').then(m => ({ default: m.GroupDetailPage })));
const GroupDetailV2Page = lazy(() => import('@ops/pages/group-detail').then(m => ({ default: m.GroupDetailV2Page })));
const FaceSheetPage = lazy(() => import('@ops/pages/face-sheet').then(m => ({ default: m.FaceSheetPage })));
const ManagedListsPage = lazy(() => import('@ops/pages/managed-lists').then(m => ({ default: m.ManagedListsPage })));
const RenewalsPage = lazy(() => import('@ops/pages/renewals').then(m => ({ default: m.RenewalsPage })));
const ImplementationOverviewPage = lazy(() => import('@ops/pages/implementation').then(m => ({ default: m.ImplementationOverviewPage })));
const ImpGanttPage = lazy(() => import('@ops/pages/implementation').then(m => ({ default: m.ImpGanttPage })));
const MyGroupsPage = lazy(() => import('@ops/pages/my-groups').then(m => ({ default: m.MyGroupsPage })));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

const OpsBasePathContext = createContext<string>('');

function buildNavItems(basePath: string): NavItem[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  return [
    { path: p('/'), label: 'Dashboard', icon: BoardRegular },
    { path: p('/my-groups'), label: 'My Groups', icon: StarRegular },
    { path: p('/intake'), label: 'Intake', icon: ClipboardTaskListLtrRegular },
    { path: p('/groups'), label: 'Groups', icon: PeopleRegular },
    { path: p('/implementations'), label: 'Implementations', icon: WrenchScrewdriverRegular },
    { path: p('/renewals'), label: 'Renewals', icon: ArrowSyncRegular },
  ];
}

/**
 * Match `/groups/<id>` anywhere in the pathname so the FAB works both at
 * `/groups/<id>` (standalone) and `/ops/groups/<id>` (shell).
 */
function ContextualQuickNoteFab() {
  const location = useLocation();
  const match = location.pathname.match(/\/groups\/([^/]+)/);
  const groupId = match?.[1];
  const { data: opsGroup } = useOpsGroup(groupId ?? '');
  return (
    <QuickNoteFab
      defaultAccountId={opsGroup?.accountId}
      defaultAccountName={opsGroup?.name}
    />
  );
}

export function OpsRootLayout() {
  const basePath = useContext(OpsBasePathContext);
  const navItems = buildNavItems(basePath);
  return (
    <ProtectedRoute appRolePrefix="Ops">
      <ErrorBoundary>
        <AppShell
          appId="ops"
          appTitle="Vera Operations"
          navItems={navItems}
          headerSearch={<HeaderGroupSearch />}
        >
          <Outlet />
          <ContextualQuickNoteFab />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Returns the Ops route tree, with sidebar links prefixed for the mount
 * path. Standalone: `opsRoutes()` at repo root. Shell: `opsRoutes('/ops')`.
 */
export function opsRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <OpsBasePathContext.Provider value={basePath}>
          <OpsRootLayout />
        </OpsBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><DashboardPage /></LazyPage> },
        { path: 'intake', element: <LazyPage><IntakePage /></LazyPage> },
        { path: 'my-groups', element: <LazyPage><MyGroupsPage /></LazyPage> },
        { path: 'groups', element: <LazyPage><GroupsPage /></LazyPage> },
        { path: 'groups/:groupId', element: <LazyPage><GroupDetailV2Page /></LazyPage> },
        { path: 'groups/:groupId/v1', element: <LazyPage><GroupDetailPage /></LazyPage> },
        { path: 'groups/:groupId/face-sheet', element: <LazyPage><FaceSheetPage /></LazyPage> },
        { path: 'implementations', element: <LazyPage><ImplementationOverviewPage /></LazyPage> },
        { path: 'implementations/:projectId/gantt', element: <LazyPage><ImpGanttPage /></LazyPage> },
        { path: 'renewals', element: <LazyPage><RenewalsPage /></LazyPage> },
        { path: 'managed-lists', element: <LazyPage><ManagedListsPage /></LazyPage> },
      ],
    },
  ];
}

